import { motion } from "framer-motion";
import { Users, MapPin, HeartHandshake, MoonStar } from "lucide-react";
import CAMPAIGN from "@/data/campaign";

const EASE = [0.76, 0, 0.24, 1];

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: EASE },
};

const SIGNALS = [
  { icon: Users, title: "Small groups", text: "Never a crowd. Travel with 12 to 16 like-minded people, not a bus of strangers." },
  { icon: MapPin, title: "Local expert guides", text: "Stories, shortcuts and local etiquette from people who call these places home." },
  { icon: HeartHandshake, title: "24/7 on-trip support", text: "Your TrekPanda trip captain travels with you from start to finish." },
  { icon: MoonStar, title: "Slow by design", text: "No 5 a.m. whistle blows. We keep room for chai, sunsets and unplanned detours." },
];

const Story = () => {
  const s = CAMPAIGN.story;

  return (
    <section id="story" className="py-24 md:py-40" data-testid="story-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-start">
          <div className="md:col-span-6 lg:col-span-5">
            <motion.p {...reveal} className="uppercase text-xs tracking-[0.25em] font-semibold text-primary mb-6" data-testid="story-overline">
              {s.overline}
            </motion.p>
            <motion.h2
              {...reveal}
              transition={{ ...reveal.transition, delay: 0.1 }}
              className="font-heading font-light tracking-tight text-foreground text-4xl sm:text-5xl leading-[1.05]"
              data-testid="story-heading"
            >
              {s.headingPlain}<br />
              <em className="text-primary">{s.headingAccent}</em>
            </motion.h2>
            <motion.div
              {...reveal}
              transition={{ ...reveal.transition, delay: 0.2 }}
              className="mt-8 space-y-5 text-base md:text-lg font-light text-foreground/80 leading-relaxed"
              data-testid="story-copy"
            >
              {s.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 60, rotate: 1.5 }}
            whileInView={{ opacity: 1, y: 0, rotate: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.1, ease: EASE }}
            className="md:col-span-6 lg:col-span-7 relative"
          >
            <div className="absolute -top-4 -left-4 md:-top-6 md:-left-6 w-full h-full border border-primary/40" aria-hidden="true" />
            <img
              src={s.image}
              alt={s.imageAlt}
              className="relative w-full h-[420px] md:h-[560px] object-cover"
              data-testid="story-image"
            />
            <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6 bg-background/90 backdrop-blur-md px-5 py-3 border border-border">
              <p className="font-heading italic text-lg text-foreground">{s.caption}</p>
            </div>
          </motion.div>
        </div>

        <div className="mt-20 md:mt-28 grid sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-border" data-testid="story-trust-signals">
          {SIGNALS.map((sig, i) => (
            <motion.div
              key={sig.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: EASE, delay: i * 0.08 }}
              className="border-r border-b border-border p-8 md:p-10 group hover:bg-secondary/60 transition-colors duration-500"
              data-testid={`trust-signal-${i}`}
            >
              <sig.icon className="w-6 h-6 text-primary mb-5 transition-transform duration-300 group-hover:-translate-y-1" />
              <h3 className="font-heading text-xl md:text-2xl font-medium text-foreground mb-2">{sig.title}</h3>
              <p className="text-sm font-light text-foreground/70 leading-relaxed">{sig.text}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mt-16 md:mt-20 flex flex-wrap justify-center gap-x-14 gap-y-8 text-center"
          data-testid="brand-stats"
        >
          {[["375K+", "Instagram family"], ["14,000+", "Google reviews"], ["15,000+", "Trips hosted"]].map(([num, label]) => (
            <div key={label}>
              <p className="font-heading font-light text-5xl md:text-6xl text-primary leading-none">{num}</p>
              <p className="mt-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Story;
